import React, { useState } from "react";
import {
    Plus,
    Edit,
    Trash2,
    Award,
    Star,
    Gift,
    Search,
    X
} from "lucide-react";

export default function EngagementRRSetup() {

    const BADGE_COLORS = {
        Gold: "bg-yellow-400",
        Silver: "bg-gray-300",
        Bronze: "bg-orange-400",
        Custom: "bg-teal-500"
    };

    const [programs, setPrograms] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");

    const [modal, setModal] = useState({
        open: false,
        isEdit: false,
        data: {
            id: null,
            name: "",
            badge: "Gold",
            points: "",
            description: "",
            active: true
        }
    });

    const openCreateModal = () => {
        setModal({
            open: true,
            isEdit: false,
            data: {
                id: null,
                name: "",
                badge: "Gold",
                points: "",
                description: "",
                active: true
            }
        });
    };

    const openEditModal = (p) => {
        setModal({
            open: true,
            isEdit: true,
            data: p
        });
    };

    const saveProgram = () => {
        const d = modal.data;

        if (!d.name.trim()) return alert("Program name is required.");
        if (!d.points || isNaN(Number(d.points))) return alert("Enter valid points.");

        let newEntry = { ...d, points: Number(d.points) };

        if (modal.isEdit) {
            setPrograms((prev) =>
                prev.map((p) => (p.id === d.id ? newEntry : p))
            );
        } else {
            setPrograms((prev) => [
                ...prev,
                { ...newEntry, id: Date.now() }
            ]);
        }

        setModal({ ...modal, open: false });
    };

    const deleteProgram = (id) => {
        if (!window.confirm("Delete this engagement program?")) return;
        setPrograms((prev) => prev.filter((p) => p.id !== id));
    };

    const filteredList = programs.filter((p) =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="p-6 md:p-10 bg-gray-50 min-h-screen font-['Poppins','Roboto',sans-serif]">

            {/* Header */}
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-800">
                        Engagement & Rewards Setup
                    </h1>
                    <p className="text-gray-500 text-sm mt-1">
                        Configure employee engagement programs, badges, and R&R reward rules.
                    </p>
                </div>

                <button
                    onClick={openCreateModal}
                    className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg shadow hover:bg-teal-700 transition"
                >
                    <Plus size={18} className="mr-2" /> Add Program
                </button>
            </div>

            {/* Search */}
            <div className="flex items-center bg-white shadow px-3 py-2 rounded-lg w-full md:w-1/3 mb-6">
                <Search size={18} className="text-gray-400 mr-2" />
                <input
                    type="text"
                    placeholder="Search engagement programs..."
                    className="outline-none w-full"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
            </div>

            {/* Program Cards */}
            <div className="bg-white p-6 rounded-xl shadow border">
                {filteredList.length === 0 ? (
                    <p className="text-center text-gray-500 py-10 italic">
                        No engagement programs created yet.
                    </p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {filteredList.map((item) => (
                            <div
                                key={item.id}
                                className="p-5 bg-white rounded-xl border shadow hover:shadow-lg transition"
                            >
                                <div className="flex items-center gap-3">
                                    <div
                                        className={`w-12 h-12 rounded-full flex items-center justify-center text-white text-xl shadow ${BADGE_COLORS[item.badge]}`}
                                    >
                                        <Star size={26} />
                                    </div>

                                    <div>
                                        <h3 className="text-lg font-semibold">{item.name}</h3>
                                        <p className="text-gray-500 text-sm">{item.badge} Badge</p>
                                    </div>
                                </div>

                                <p className="mt-3 text-gray-700 text-sm">
                                    {item.description || "No description provided."}
                                </p>

                                <p className="mt-4 text-sm">
                                    Reward Points:{" "}
                                    <span className="font-semibold text-indigo-600">
                                        {item.points}
                                    </span>
                                </p>

                                <p className="mt-1 text-sm">
                                    Status:{" "}
                                    <span
                                        className={`font-semibold ${
                                            item.active ? "text-green-600" : "text-red-600"
                                        }`}
                                    >
                                        {item.active ? "Active" : "Inactive"}
                                    </span>
                                </p>

                                <div className="flex justify-between mt-5">
                                    <button
                                        onClick={() => openEditModal(item)}
                                        className="flex items-center text-indigo-600 hover:text-indigo-800"
                                    >
                                        <Edit size={18} className="mr-1" /> Edit
                                    </button>

                                    <button
                                        onClick={() => deleteProgram(item.id)}
                                        className="text-red-600 hover:text-red-800"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modal */}
            {modal.open && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-6">
                    <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-3xl relative animate-fadeIn">

                        <button
                            onClick={() => setModal({ ...modal, open: false })}
                            className="absolute right-5 top-5 text-gray-600 hover:text-black"
                        >
                            <X size={24} />
                        </button>

                        <h2 className="text-xl font-semibold mb-4">
                            {modal.isEdit ? "Edit Program" : "Add New Engagement Program"}
                        </h2>

                        {/* Form */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                            {/* Program Name */}
                            <div>
                                <label className="text-sm text-gray-600">Program Name</label>
                                <input
                                    type="text"
                                    className="border rounded p-2 w-full mt-1"
                                    value={modal.data.name}
                                    onChange={(e) =>
                                        setModal({
                                            ...modal,
                                            data: { ...modal.data, name: e.target.value }
                                        })
                                    }
                                />
                            </div>

                            {/* Badge Type */}
                            <div>
                                <label className="text-sm text-gray-600">Badge Type</label>
                                <select
                                    className="border rounded p-2 w-full mt-1"
                                    value={modal.data.badge}
                                    onChange={(e) =>
                                        setModal({
                                            ...modal,
                                            data: { ...modal.data, badge: e.target.value }
                                        })
                                    }
                                >
                                    {Object.keys(BADGE_COLORS).map((b) => (
                                        <option key={b}>{b}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Points */}
                            <div>
                                <label className="text-sm text-gray-600">Reward Points</label>
                                <input
                                    type="number"
                                    className="border rounded p-2 w-full mt-1"
                                    placeholder="Example: 50"
                                    value={modal.data.points}
                                    onChange={(e) =>
                                        setModal({
                                            ...modal,
                                            data: { ...modal.data, points: e.target.value }
                                        })
                                    }
                                />
                            </div>

                            {/* Status */}
                            <div>
                                <label className="text-sm text-gray-600">Status</label>
                                <select
                                    className="border rounded p-2 w-full mt-1"
                                    value={modal.data.active}
                                    onChange={(e) =>
                                        setModal({
                                            ...modal,
                                            data: {
                                                ...modal.data,
                                                active: e.target.value === "true"
                                            }
                                        })
                                    }
                                >
                                    <option value="true">Active</option>
                                    <option value="false">Inactive</option>
                                </select>
                            </div>

                            {/* Description */}
                            <div className="md:col-span-2">
                                <label className="text-sm text-gray-600">Description</label>
                                <textarea
                                    className="border rounded p-2 w-full mt-1"
                                    rows="3"
                                    placeholder="Describe how this engagement program works..."
                                    value={modal.data.description}
                                    onChange={(e) =>
                                        setModal({
                                            ...modal,
                                            data: { ...modal.data, description: e.target.value }
                                        })
                                    }
                                ></textarea>
                            </div>
                        </div>

                        {/* Buttons */}
                        <div className="flex justify-end gap-3 mt-5">
                            <button
                                onClick={() => setModal({ ...modal, open: false })}
                                className="px-4 py-2 bg-gray-200 rounded-lg"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={saveProgram}
                                className="px-4 py-2 bg-teal-600 text-white rounded-lg"
                            >
                                {modal.isEdit ? "Save Changes" : "Add Program"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}
